import {
  Label,
  LocalizedTextList,
  ProviderCredentialEvidence,
  ProviderEvidenceFile,
  ProviderProfessionalAttributes,
  ProviderWorkExperience,
  SpecialistData,
  SubSpecializations,
} from "../types/TypeSpecialist";
import { areValuesEqual, FieldChange } from "./profileDiffUtils";

const EMPTY_VALUE = "-";

export type FormattedLocalizedText = {
  language: string;
  text: string;
};

export type FormattedLocalizedList = {
  language: string;
  items: string[];
};

export type FormattedProviderWorkExperience = {
  companyName: FormattedLocalizedText[];
  jobTitle: FormattedLocalizedText[];
  mainFunctions: FormattedLocalizedList[];
  startDate: string;
  endDate: string;
  subSpecializations: string[];
};

export type FormattedProviderProfessionalAttributes = {
  details: { label: string; value: string }[];
  hoursBySubSpecialization: { subSpecialization: string; hours: string }[];
  casesBySubSpecialization: { subSpecialization: string; cases: string }[];
  methodology: {
    subSpecialization: string;
    description: FormattedLocalizedText[];
  }[];
  industryFocus: FormattedLocalizedList[];
};

export type FormattedProviderEvidenceFile = {
  originalName: string;
  mimeType: string;
  size: string;
  uploadedAt: string;
  filepath: string;
  canOpen: boolean;
};

export type FormattedProviderCredential = {
  credentialType: string;
  details: { label: string; value: string }[];
  proofFile?: FormattedProviderEvidenceFile;
  rawProofFile?: ProviderEvidenceFile;
};

export type FormattedModeratorOnlyEvidenceField = {
  key: string;
  label: string;
  values: { label?: string; value: string }[];
  file?: FormattedProviderEvidenceFile;
  rawFile?: ProviderEvidenceFile;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const uniq = (values: string[]) => Array.from(new Set(values));

const providerEvidenceOptionLabels: Record<string, string> = {
  adults: "Adults",
  adolescents: "Adolescents",
  academicDietitian: "Academic dietitian",
  attorneyMediator: "Attorney mediator",
  candidateOfSciences: "Candidate of sciences",
  certifiedMentor: "Certified mentor",
  children: "Children",
  clinicalDietitian: "Clinical dietitian",
  consultantDietitian: "Consultant dietitian",
  couples: "Couples",
  dietitian: "Dietitian",
  doctorOfSciences: "Doctor of sciences",
  dsm5: "DSM-5",
  facilitator: "Facilitator",
  families: "Families",
  geriatricPatients: "Geriatric patients",
  groups: "Groups",
  healthCoach: "Health coach",
  icd10: "ICD-10",
  icd11: "ICD-11",
  insolvencyPractitioner: "Insolvency practitioner",
  insolvencyPractitionerRegistry: "Insolvency practitioner registry",
  lawyer: "Lawyer",
  lawyerRegistry: "Lawyer registry",
  legalConsultant: "Legal consultant",
  medicalLicense: "Medical license",
  mediator: "Mediator",
  mentorPractitioner: "Mentor practitioner",
  negotiator: "Negotiator",
  notary: "Notary",
  notaryRegistry: "Notary registry",
  nutritionExpert: "Nutrition expert",
  nutritionist: "Nutritionist",
  other: "Other",
  phd: "PhD",
};

const moderatorOnlyProviderEvidenceLabels: Record<string, string> = {
  informedConsent: "Informed consent",
  personalTherapyHours: "Personal therapy hours",
  supervisionHours: "Supervision hours",
  supervisorContact: "Supervisor contact",
};

export const toReadableProviderEvidenceLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^./, (first) => first.toUpperCase());

export const getProviderEvidenceOptionReviewLabel = (key: string) =>
  providerEvidenceOptionLabels[key] || toReadableProviderEvidenceLabel(key);

export const getModeratorOnlyProviderEvidenceReviewLabel = (key: string) =>
  moderatorOnlyProviderEvidenceLabels[key] ||
  toReadableProviderEvidenceLabel(key);

const toDisplayDate = (value?: string) => {
  if (!isNonEmptyString(value)) return EMPTY_VALUE;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const toDisplayDateTime = (value?: string) => {
  if (!isNonEmptyString(value)) return EMPTY_VALUE;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const formatFileSizeForReview = (size?: number) => {
  if (typeof size !== "number" || !Number.isFinite(size)) return EMPTY_VALUE;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatLocalizedTextForReview = (
  value: Label[] | undefined
): FormattedLocalizedText[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return [{ language: "unknown", text: EMPTY_VALUE }];
  }

  return value.map((item) => ({
    language: isNonEmptyString(item.language) ? item.language : "unknown",
    text: isNonEmptyString(item.text) ? item.text : EMPTY_VALUE,
  }));
};

export const formatLocalizedListForReview = (
  value: LocalizedTextList[] | undefined
): FormattedLocalizedList[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return [{ language: "unknown", items: [EMPTY_VALUE] }];
  }

  return value.map((item) => ({
    language: isNonEmptyString(item.language) ? item.language : "unknown",
    items:
      Array.isArray(item.items) && item.items.length > 0
        ? item.items.map((text) => (isNonEmptyString(text) ? text : EMPTY_VALUE))
        : [EMPTY_VALUE],
  }));
};

const getIdValue = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "object" && "_id" in value) {
    const id = (value as { _id?: unknown })._id;
    return typeof id === "string" ? id : id?.toString();
  }
  return value.toString();
};

export const getSubSpecializationReviewLabel = (
  subSpecializations: SubSpecializations[] = [],
  value: unknown
) => {
  const id = getIdValue(value);
  if (!id) return EMPTY_VALUE;

  const subSpecialization = subSpecializations.find((item) => item._id === id);
  return subSpecialization?.specialization || id;
};

export const formatProviderWorkExperienceForReview = (
  workExperience: ProviderWorkExperience[] | undefined,
  subSpecializations: SubSpecializations[] = []
): FormattedProviderWorkExperience[] =>
  (workExperience ?? []).map((entry) => ({
    companyName: formatLocalizedTextForReview(entry.companyName),
    jobTitle: formatLocalizedTextForReview(entry.jobTitle),
    mainFunctions: formatLocalizedListForReview(entry.mainFunctions),
    startDate: toDisplayDate(entry.startDate),
    endDate: entry.endDate ? toDisplayDate(entry.endDate) : "Present",
    subSpecializations:
      entry.subSpecializations && entry.subSpecializations.length > 0
        ? entry.subSpecializations.map((item) =>
            getSubSpecializationReviewLabel(subSpecializations, item)
          )
        : [EMPTY_VALUE],
  }));

export const hasProviderWorkExperienceValue = (
  value: ProviderWorkExperience[] | undefined
) => Array.isArray(value) && value.length > 0;

const formatOptionList = (values?: string[]) =>
  values && values.length > 0
    ? values.map(getProviderEvidenceOptionReviewLabel).join(", ")
    : EMPTY_VALUE;

const formatBoolean = (value?: boolean) => {
  if (typeof value !== "boolean") return EMPTY_VALUE;
  return value ? "Yes" : "No";
};

const formatNumber = (value?: number) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toString()
    : EMPTY_VALUE;

export const hasProviderProfessionalAttributesValue = (
  value: ProviderProfessionalAttributes | undefined
) => {
  if (!value || typeof value !== "object") return false;

  return Object.values(value).some((item) =>
    Array.isArray(item) ? item.length > 0 : item !== undefined && item !== null
  );
};

export const hasProviderCredentialsValue = (
  value: ProviderCredentialEvidence[] | undefined
) => Array.isArray(value) && value.length > 0;

export const formatProviderEvidenceFileForReview = (
  file: ProviderEvidenceFile | undefined
): FormattedProviderEvidenceFile | undefined => {
  if (!file) return undefined;

  return {
    originalName: isNonEmptyString(file.originalName)
      ? file.originalName
      : EMPTY_VALUE,
    mimeType: isNonEmptyString(file.mimeType) ? file.mimeType : EMPTY_VALUE,
    size: formatFileSizeForReview(file.size),
    uploadedAt: toDisplayDateTime(file.uploadedAt),
    filepath: isNonEmptyString(file.filepath) ? file.filepath : EMPTY_VALUE,
    canOpen: Boolean(file.url || file.filepath),
  };
};

export const isProviderEvidenceFile = (
  value: unknown
): value is ProviderEvidenceFile =>
  Boolean(
    value &&
      typeof value === "object" &&
      "filepath" in value &&
      "originalName" in value &&
      "mimeType" in value &&
      "size" in value &&
      "uploadedAt" in value
  );

const isLocalizedTextArray = (
  value: unknown
): value is { language: string; text: string }[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      item &&
      typeof item === "object" &&
      "language" in item &&
      "text" in item
  );

const formatUnknownValueForReview = (value: unknown): string => {
  if (value === undefined || value === null) return EMPTY_VALUE;
  if (typeof value === "string") return isNonEmptyString(value) ? value : EMPTY_VALUE;
  if (typeof value === "number") return formatNumber(value);
  if (typeof value === "boolean") return formatBoolean(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_VALUE;
    return value
      .map((item) =>
        typeof item === "string" || typeof item === "number" || typeof item === "boolean"
          ? formatUnknownValueForReview(item)
          : JSON.stringify(item)
      )
      .join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return EMPTY_VALUE;
    return entries
      .map(
        ([key, item]) =>
          `${toReadableProviderEvidenceLabel(key)}: ${formatUnknownValueForReview(
            item
          )}`
      )
      .join("; ");
  }

  return String(value);
};

export const hasModeratorOnlyProviderEvidenceValue = (
  value: Record<string, unknown | ProviderEvidenceFile> | undefined
) => {
  if (!value || typeof value !== "object") return false;

  return Object.values(value).some((item) => {
    if (isProviderEvidenceFile(item)) return true;
    if (Array.isArray(item)) return item.length > 0;
    if (typeof item === "string") return item.trim().length > 0;
    if (item && typeof item === "object") return Object.keys(item).length > 0;
    return item !== undefined && item !== null;
  });
};

export const formatModeratorOnlyProviderEvidenceForReview = (
  evidence: Record<string, unknown | ProviderEvidenceFile> | undefined
): FormattedModeratorOnlyEvidenceField[] =>
  Object.entries(evidence ?? {}).map(([key, value]) => {
    if (isProviderEvidenceFile(value)) {
      return {
        key,
        label: getModeratorOnlyProviderEvidenceReviewLabel(key),
        values: [],
        file: formatProviderEvidenceFileForReview(value),
        rawFile: value,
      };
    }

    if (isLocalizedTextArray(value)) {
      return {
        key,
        label: getModeratorOnlyProviderEvidenceReviewLabel(key),
        values: formatLocalizedTextForReview(value).map((item) => ({
          label: item.language.toUpperCase(),
          value: item.text,
        })),
      };
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      const entries = Object.entries(value as Record<string, unknown>);
      return {
        key,
        label: getModeratorOnlyProviderEvidenceReviewLabel(key),
        values:
          entries.length > 0
            ? entries.map(([entryKey, entryValue]) => ({
                label: toReadableProviderEvidenceLabel(entryKey),
                value: formatUnknownValueForReview(entryValue),
              }))
            : [{ value: EMPTY_VALUE }],
      };
    }

    return {
      key,
      label: getModeratorOnlyProviderEvidenceReviewLabel(key),
      values: [{ value: formatUnknownValueForReview(value) }],
    };
  });

export const formatProviderCredentialsForReview = (
  credentials: ProviderCredentialEvidence[] | undefined
): FormattedProviderCredential[] =>
  (credentials ?? []).map((credential) => {
    const details: { label: string; value: string }[] = [
      {
        label: "Credential type",
        value: getProviderEvidenceOptionReviewLabel(
          credential.credentialType || "unknown"
        ),
      },
    ];

    if (isNonEmptyString(credential.identifier)) {
      details.push({ label: "Identifier", value: credential.identifier });
    }

    if (isNonEmptyString(credential.issuingOrganization)) {
      details.push({
        label: "Issuing organization",
        value: credential.issuingOrganization,
      });
    }

    if (isNonEmptyString(credential.jurisdictionCountry)) {
      details.push({
        label: "Jurisdiction country",
        value: credential.jurisdictionCountry,
      });
    }

    if (isNonEmptyString(credential.jurisdictionDetails)) {
      details.push({
        label: "Jurisdiction details",
        value: credential.jurisdictionDetails,
      });
    }

    if (isNonEmptyString(credential.issueDate)) {
      details.push({
        label: "Issue date",
        value: toDisplayDate(credential.issueDate),
      });
    }

    if (isNonEmptyString(credential.expirationDate)) {
      details.push({
        label: "Expiration date",
        value: toDisplayDate(credential.expirationDate),
      });
    }

    return {
      credentialType: getProviderEvidenceOptionReviewLabel(
        credential.credentialType || "unknown"
      ),
      details,
      proofFile: formatProviderEvidenceFileForReview(credential.proofFile),
      rawProofFile: credential.proofFile,
    };
  });

export const formatProviderProfessionalAttributesForReview = (
  attributes: ProviderProfessionalAttributes | undefined,
  subSpecializations: SubSpecializations[] = []
): FormattedProviderProfessionalAttributes => {
  const details: { label: string; value: string }[] = [];

  if (attributes?.professionalStatus?.length) {
    details.push({
      label: "Professional status",
      value: formatOptionList(attributes.professionalStatus),
    });
  }

  if (attributes?.targetAudience?.length) {
    details.push({
      label: "Target audience",
      value: formatOptionList(attributes.targetAudience),
    });
  }

  if (attributes?.clinicalDiagnostics?.length) {
    details.push({
      label: "Clinical diagnostics",
      value: formatOptionList(attributes.clinicalDiagnostics),
    });
  }

  const academicDegree = attributes?.academicDegree;
  if (isNonEmptyString(academicDegree)) {
    details.push({
      label: "Academic degree",
      value: getProviderEvidenceOptionReviewLabel(academicDegree),
    });
  }

  const primaryJurisdictionCountry = attributes?.primaryJurisdictionCountry;
  if (isNonEmptyString(primaryJurisdictionCountry)) {
    details.push({
      label: "Jurisdiction country",
      value: primaryJurisdictionCountry,
    });
  }

  const primaryJurisdictionDetails = attributes?.primaryJurisdictionDetails;
  if (isNonEmptyString(primaryJurisdictionDetails)) {
    details.push({
      label: "Jurisdiction details",
      value: primaryJurisdictionDetails,
    });
  }

  if (typeof attributes?.pharmacotherapy === "boolean") {
    details.push({
      label: "Pharmacotherapy",
      value: formatBoolean(attributes.pharmacotherapy),
    });
  }

  if (attributes?.totalProviderHours !== undefined) {
    details.push({
      label: "Total provider hours",
      value: formatNumber(attributes.totalProviderHours),
    });
  }

  return {
    details:
      details.length > 0 ? details : [{ label: "Attributes", value: EMPTY_VALUE }],
    hoursBySubSpecialization: (
      attributes?.hoursBySubSpecialization ?? []
    ).map((item) => ({
      subSpecialization: getSubSpecializationReviewLabel(
        subSpecializations,
        item.subSpecialization
      ),
      hours: formatNumber(item.hours),
    })),
    casesBySubSpecialization: (
      attributes?.casesBySubSpecialization ?? []
    ).map((item) => ({
      subSpecialization: getSubSpecializationReviewLabel(
        subSpecializations,
        item.subSpecialization
      ),
      cases: formatNumber(item.cases),
    })),
    methodology: (attributes?.methodology ?? []).map((item) => ({
      subSpecialization: item.subSpecialization
        ? getSubSpecializationReviewLabel(
            subSpecializations,
            item.subSpecialization
          )
        : EMPTY_VALUE,
      description: formatLocalizedTextForReview(item.description ?? item.text),
    })),
    industryFocus: formatLocalizedListForReview(attributes?.industryFocus),
  };
};

export const compareProviderWorkExperienceField = (
  publicSpec: SpecialistData,
  updatedSpec: SpecialistData
): FieldChange | null => {
  const oldValue = publicSpec.workExperience;
  const newValue = updatedSpec.workExperience;
  const hasChanges = !areValuesEqual(oldValue, newValue);

  if (!hasChanges) return null;

  const oldCount = oldValue?.length ?? 0;
  const newCount = newValue?.length ?? 0;
  const changedLanguages = uniq(
    (newValue ?? [])
      .flatMap((entry) => [
        ...(entry.companyName ?? []).map((item) => item.language),
        ...(entry.jobTitle ?? []).map((item) => item.language),
        ...(entry.mainFunctions ?? []).map((item) => item.language),
      ])
      .filter(isNonEmptyString)
  );

  return {
    field: "workExperience",
    label: "Work Experience",
    oldValue: oldValue ?? [],
    newValue: newValue ?? [],
    category: "professional",
    severity: "major",
    isProviderWorkExperience: true,
    isArray: true,
    changeType:
      oldCount === 0 && newCount > 0
        ? "added"
        : oldCount > 0 && newCount === 0
        ? "removed"
        : "modified",
    summary:
      changedLanguages.length > 0
        ? `${oldCount} current, ${newCount} pending (${changedLanguages.join(
            ", "
          )})`
        : `${oldCount} current, ${newCount} pending`,
  };
};

export const compareProviderProfessionalAttributesField = (
  publicSpec: SpecialistData,
  updatedSpec: SpecialistData
): FieldChange | null => {
  const oldValue = publicSpec.providerProfessionalAttributes;
  const newValue = updatedSpec.providerProfessionalAttributes;
  const hasChanges = !areValuesEqual(oldValue, newValue);

  if (!hasChanges) return null;

  return {
    field: "providerProfessionalAttributes",
    label: "Professional Attributes",
    oldValue: oldValue ?? {},
    newValue: newValue ?? {},
    category: "professional",
    severity: "major",
    isProviderProfessionalAttributes: true,
    changeType:
      !hasProviderProfessionalAttributesValue(oldValue) &&
      hasProviderProfessionalAttributesValue(newValue)
        ? "added"
        : hasProviderProfessionalAttributesValue(oldValue) &&
          !hasProviderProfessionalAttributesValue(newValue)
        ? "removed"
        : "modified",
    summary: hasProviderProfessionalAttributesValue(newValue)
      ? "Pending professional attributes available for review"
      : "Professional attributes removed",
  };
};

export const compareProviderCredentialsField = (
  publicSpec: SpecialistData,
  updatedSpec: SpecialistData
): FieldChange | null => {
  const oldValue = publicSpec.providerCredentials;
  const newValue = updatedSpec.providerCredentials;
  const hasChanges = !areValuesEqual(oldValue, newValue);

  if (!hasChanges) return null;

  const oldCount = oldValue?.length ?? 0;
  const newCount = newValue?.length ?? 0;

  return {
    field: "providerCredentials",
    label: "Provider Credentials",
    oldValue: oldValue ?? [],
    newValue: newValue ?? [],
    category: "professional",
    severity: "major",
    isProviderCredentials: true,
    isArray: true,
    changeType:
      oldCount === 0 && newCount > 0
        ? "added"
        : oldCount > 0 && newCount === 0
        ? "removed"
        : "modified",
    summary: `${oldCount} current, ${newCount} pending`,
  };
};

export const compareModeratorOnlyProviderEvidenceField = (
  publicSpec: SpecialistData,
  updatedSpec: SpecialistData
): FieldChange | null => {
  const oldValue = publicSpec.moderatorOnlyProviderEvidence;
  const newValue = updatedSpec.moderatorOnlyProviderEvidence;
  const hasChanges = !areValuesEqual(oldValue, newValue);

  if (!hasChanges) return null;

  const oldCount = Object.keys(oldValue ?? {}).length;
  const newCount = Object.keys(newValue ?? {}).length;

  return {
    field: "moderatorOnlyProviderEvidence",
    label: "Moderator-only Provider Evidence",
    oldValue: oldValue ?? {},
    newValue: newValue ?? {},
    category: "professional",
    severity: "major",
    isModeratorOnlyProviderEvidence: true,
    changeType:
      oldCount === 0 && newCount > 0
        ? "added"
        : oldCount > 0 && newCount === 0
        ? "removed"
        : "modified",
    summary: `${oldCount} current, ${newCount} pending moderation-only field(s)`,
  };
};
